/**
 * A ticket (TMMPDM-0109; TMMAPI-0181 U5): `/tickets/:number`, where the number may carry its type's
 * prefix (`SC-1001`). The header shows its number, title, type, status, tenant (linked to its partner
 * page), requester, times, and — as sent — "Duplicate of …", "{{n}} followers" and "Shipped in …".
 * Two tabs (URL `tab`): Conversation (every update, oldest first, with the composer under it) and
 * Details. An internal note sits on its own surface, labelled so; a status change is one compact line.
 * A session the ticket was drafted in is never shown, only that it was.
 * TMMPDM-0110 (U7): files the server lists on the ticket (Details) or on an update are listed and
 * downloaded only (`TicketAttachments`); none is ever rendered. The API is addressed by the bare
 * number (`1001`), never by "SC-1001".
 *
 * Until tmm-api deploys U5 a ticket answers a bare 404: "Tickets aren't available yet.", no retry.
 * A deployed route's `TICKET_NOT_FOUND` (or a number that can't be one) is "This ticket doesn't exist."
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useReturnTo } from '@/hooks/useReturnTo';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { adminApi } from '@/api/tmm/admin';
import type { TicketUpdateView, TicketView } from '@/api/tmm/types';
import { EmptyState, ErrorState, ObjectHeader, SectionCard } from '@/components/page';
import type { HeaderAction } from '@/components/page/headerChrome';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { FilterTabs } from '@/components/ui';
import { formatInstant, formatNumber } from '@/format';
import { StatusChip } from '@/status';
import { TicketComposer } from './TicketComposer';
import { TicketAttachments } from './TicketAttachments';
import { ChangeStatusDialog, MarkDuplicateDialog, ShippedInDialog } from './TicketActionDialogs';
import {
  isNotDeployed,
  isTicketNotFound,
  notAvailableText,
  parseTicketNumber,
  requesterOf,
  statusLabel,
  tenantNameOf,
  tenantPath,
  ticketRef,
  typeLabel,
  type Translate,
} from './tickets';

const TICKET_TABS = ['conversation', 'details'] as const;
type TicketPageTab = (typeof TICKET_TABS)[number];
const VIEW: UrlSchema<{ tab: TicketPageTab }> = {
  tab: { default: 'conversation', parse: oneOf(TICKET_TABS) },
};

type Load =
  | { state: 'loading' }
  | { state: 'loaded'; ticket: TicketView }
  | { state: 'notFound' }
  | { state: 'notDeployed' }
  | { state: 'failed'; failure: unknown };

type DialogKind = 'status' | 'duplicate' | 'shippedIn';

/** A ticket names its own tenant; without a name (or with an empty one) it shows the slug. */
const NO_TENANT_NAMES: ReadonlyMap<string, string> = new Map();

export function TicketPage() {
  const { number: param = '' } = useParams();
  return <TicketPageContent key={param} param={param} />;
}

function TicketPageContent({ param }: { param: string }) {
  const number = parseTicketNumber(param);
  const { t } = useCopy();
  const { backTo } = useReturnTo();
  const [view, setView] = useUrlState(VIEW);
  const [reloadToken, setReloadToken] = useState(0);
  const [load, setLoad] = useState<Load>(
    number === null ? { state: 'notFound' } : { state: 'loading' },
  );
  const [dialog, setDialog] = useState<DialogKind | null>(null);

  useEffect(() => {
    if (number === null) return;
    let cancelled = false;
    adminApi
      .getTicket(number)
      .then((ticket) => {
        if (!cancelled) setLoad({ state: 'loaded', ticket });
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        if (isTicketNotFound(failure)) setLoad({ state: 'notFound' });
        else if (isNotDeployed(failure)) setLoad({ state: 'notDeployed' });
        else setLoad({ state: 'failed', failure });
      });
    return () => {
      cancelled = true;
    };
  }, [number, reloadToken]);

  // A re-read keeps the ticket on screen until the new one arrives, so nothing under the pointer moves.
  const reread = useCallback(() => setReloadToken((n) => n + 1), []);
  const done = () => {
    setDialog(null);
    reread();
  };

  const ticketsCrumb = { label: t('page.tickets.title', 'Support'), to: backTo('/tickets') };

  // One header element for every state (TMM-B7): the h1's text changes, the element stays.
  if (load.state !== 'loaded') {
    const title =
      load.state === 'notFound'
        ? t('tickets.notFound', "This ticket doesn't exist.")
        : t('tickets.ticket', 'Ticket');
    return (
      <Box>
        <ObjectHeader breadcrumbs={[ticketsCrumb, { label: param || title }]} name={title} />
        {load.state === 'loading' ? <LoadingSpinner /> : null}
        {load.state === 'notDeployed' ? <EmptyState surface title={notAvailableText(t)} /> : null}
        {load.state === 'failed' ? (
          <ErrorState
            error={load.failure}
            message={t('tickets.ticketLoadError', 'Could not load this ticket.')}
            onRetry={reread}
          />
        ) : null}
      </Box>
    );
  }

  const ticket = load.ticket;
  const ref = ticketRef(ticket.type, ticket.number);
  const features = ticket.type === 'FEATURE_REQUEST';
  const secondaryActions: HeaderAction[] = features
    ? [
        // Hidden on a ticket that is already a duplicate: its own `duplicateOf` says so.
        ...(ticket.duplicateOf === undefined
          ? [
              {
                label: t('tickets.duplicate.action', 'Mark as duplicate…'),
                onClick: () => setDialog('duplicate'),
              },
            ]
          : []),
        {
          label: t('tickets.shippedIn.action', 'Set "shipped in"…'),
          onClick: () => setDialog('shippedIn'),
        },
      ]
    : [];

  return (
    <Box>
      <ObjectHeader
        breadcrumbs={[ticketsCrumb, { label: ref }]}
        name={ticket.title}
        identifier={ref}
        status={
          <>
            <StatusChip status={ticket.status} label={statusLabel(ticket.status, t)} />
            <Typography variant="body2" color="text.secondary">
              {typeLabel(ticket.type, t)}
            </Typography>
          </>
        }
        meta={<TicketMeta ticket={ticket} />}
        primaryAction={{
          label: t('tickets.changeStatus.action', 'Change status…'),
          onClick: () => setDialog('status'),
        }}
        secondaryActions={secondaryActions}
      />

      <FilterTabs
        ariaLabel={t('tickets.page.tabsAria', 'Ticket sections')}
        active={view.tab}
        onChange={(key) => {
          const tab = oneOf(TICKET_TABS)(key);
          if (tab) setView({ tab });
        }}
        tabs={[
          { key: 'conversation', label: t('tickets.page.conversation', 'Conversation') },
          { key: 'details', label: t('tickets.page.details', 'Details') },
        ]}
      />
      <Box sx={{ mt: 2 }}>
        {view.tab === 'conversation' ? (
          <SectionCard title={t('tickets.page.conversation', 'Conversation')}>
            <Stack spacing={2}>
              <Conversation number={ticket.number} updates={ticket.updates} />
              <TicketComposer number={ticket.number} onPosted={reread} />
            </Stack>
          </SectionCard>
        ) : (
          <TicketDetails ticket={ticket} />
        )}
      </Box>

      <ChangeStatusDialog
        open={dialog === 'status'}
        ticket={ticket}
        onClose={() => setDialog(null)}
        onDone={done}
      />
      {features ? (
        <>
          <MarkDuplicateDialog
            open={dialog === 'duplicate'}
            ticket={ticket}
            onClose={() => setDialog(null)}
            onDone={done}
          />
          <ShippedInDialog
            open={dialog === 'shippedIn'}
            ticket={ticket}
            onClose={() => setDialog(null)}
            onDone={done}
          />
        </>
      ) : null}
    </Box>
  );
}

/** The header's lines: tenant, requester, times, and the duplicate, followers and shipped-in as sent. */
function TicketMeta({ ticket }: { ticket: TicketView }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  return (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        <Link component={RouterLink} to={tenantPath(ticket.tenantSlug)}>
          {tenantNameOf(ticket, NO_TENANT_NAMES)}
        </Link>
        {' · '}
        {t('tickets.meta.requester', 'Asked by {{person}}', { person: requesterOf(ticket) })}
        {' · '}
        {t('tickets.meta.created', 'Created {{when}}', {
          when: formatInstant(ticket.createdAt, locale),
        })}
        {' · '}
        {t('tickets.meta.updated', 'Updated {{when}}', {
          when: formatInstant(ticket.updatedAt, locale),
        })}
      </Typography>
      {ticket.duplicateOf !== undefined || ticket.followers !== undefined || ticket.shippedIn ? (
        <Typography variant="body2" color="text.secondary">
          {ticket.duplicateOf !== undefined ? (
            <DuplicateOf type={ticket.type} number={ticket.duplicateOf} />
          ) : null}
          {ticket.followers !== undefined ? (
            <Box component="span" sx={{ ml: ticket.duplicateOf !== undefined ? 2 : 0 }}>
              {t('tickets.meta.followers', '{{count, number}} followers', {
                count: ticket.followers,
              })}
            </Box>
          ) : null}
          {ticket.shippedIn ? (
            <Box component="span" sx={{ ml: 2 }}>
              {t('tickets.meta.shippedIn', 'Shipped in {{shippedIn}}', {
                shippedIn: ticket.shippedIn,
              })}
            </Box>
          ) : null}
        </Typography>
      ) : null}
    </Stack>
  );
}

/** "Duplicate of FR-1002", linked. A duplicate is a feature request, so the canonical one is too. */
function DuplicateOf({ type, number }: { type: string; number: number }) {
  const { t } = useCopy();
  const canonical = ticketRef(type, number);
  return (
    <>
      {t('tickets.meta.duplicateOf', 'Duplicate of')}{' '}
      <Link component={RouterLink} to={`/tickets/${encodeURIComponent(canonical)}`}>
        {canonical}
      </Link>
    </>
  );
}

/** Every update, oldest first. */
function Conversation({ number, updates }: { number: number; updates: TicketUpdateView[] }) {
  const { t } = useCopy();
  const ordered = [...updates].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id,
  );
  if (ordered.length === 0) {
    // Inside the Conversation card, which is already the surface.
    return <EmptyState title={t('tickets.conversation.empty', 'No updates yet.')} />;
  }
  return (
    <Stack component="ol" spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {ordered.map((update) => (
        <Box component="li" key={update.id}>
          <UpdateItem number={number} update={update} />
        </Box>
      ))}
    </Stack>
  );
}

function authorLabel(update: TicketUpdateView, t: Translate): string {
  const kind =
    update.authorKind === 'MEMBER'
      ? t('tickets.author.MEMBER', 'Member')
      : update.authorKind === 'STAFF'
        ? t('tickets.author.STAFF', 'Staff')
        : update.authorKind === 'SYSTEM'
          ? t('tickets.author.SYSTEM', 'System')
          : String(update.authorKind);
  return update.authorName ? `${update.authorName} (${kind})` : kind;
}

function UpdateItem({ number, update }: { number: number; update: TicketUpdateView }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const when = formatInstant(update.createdAt, locale);
  const internal = update.visibility === 'INTERNAL';

  // A status change: one compact line.
  if (update.authorKind === 'SYSTEM' && !internal) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
        {when}
        {' · '}
        {update.toStatus !== undefined
          ? t('tickets.conversation.statusChanged', 'Status changed to {{status}}', {
              status: statusLabel(update.toStatus, t),
            })
          : update.body}
      </Typography>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={
        internal
          ? {
              p: 2,
              bgcolor: 'background.default',
              borderLeftWidth: 4,
              borderLeftColor: 'warning.main',
            }
          : { p: 2 }
      }
    >
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
      >
        <Typography variant="subtitle2" component="p">
          {authorLabel(update, t)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {when}
        </Typography>
        <Typography
          variant="body2"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
        >
          {internal ? (
            <>
              <LockOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              {t('tickets.internalNote', 'Internal note: the member never sees this.')}
            </>
          ) : (
            t('tickets.conversation.public', 'Public: the member sees this')
          )}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {update.body}
      </Typography>
      {update.attachments !== undefined && update.attachments.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2" component="p" sx={{ mb: 0.5 }}>
            {t('tickets.attachments.onUpdate', 'Attached files')}
          </Typography>
          <TicketAttachments
            number={number}
            attachments={update.attachments}
            label={t('tickets.attachments.onUpdate', 'Attached files')}
          />
        </Box>
      ) : null}
    </Paper>
  );
}

function TicketDetails({ ticket }: { ticket: TicketView }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const rows: Array<[string, ReactNode]> = [
    [t('tickets.details.type', 'Type'), typeLabel(ticket.type, t)],
    [t('tickets.col.status', 'Status'), statusLabel(ticket.status, t)],
    [
      t('tickets.col.tenant', 'Tenant'),
      <Link key="tenant" component={RouterLink} to={tenantPath(ticket.tenantSlug)}>
        {tenantNameOf(ticket, NO_TENANT_NAMES)}
      </Link>,
    ],
    [t('tickets.col.requester', 'Requester'), requesterOf(ticket)],
    ...(ticket.requesterEmail && ticket.requesterName
      ? [[t('tickets.details.email', 'Email'), ticket.requesterEmail] as [string, string]]
      : []),
    [t('tickets.col.created', 'Created'), formatInstant(ticket.createdAt, locale)],
    [t('tickets.col.updated', 'Updated'), formatInstant(ticket.updatedAt, locale)],
    ...(ticket.duplicateOf !== undefined
      ? [
          [
            t('tickets.meta.duplicateOf', 'Duplicate of'),
            ticketRef(ticket.type, ticket.duplicateOf),
          ] as [string, string],
        ]
      : []),
    ...(ticket.followers !== undefined
      ? [
          [t('tickets.col.followers', 'Followers'), formatNumber(ticket.followers, locale)] as [
            string,
            string,
          ],
        ]
      : []),
    ...(ticket.shippedIn
      ? [[t('tickets.shippedIn.label', 'Shipped in'), ticket.shippedIn] as [string, string]]
      : []),
    ...(ticket.draftTurnId !== undefined
      ? [
          [
            t('tickets.details.origin', 'Origin'),
            t('tickets.details.drafted', 'Drafted with the assistant'),
          ] as [string, string],
        ]
      : []),
  ];
  return (
    <Stack spacing={2}>
      <SectionCard title={t('tickets.details.description', 'Description')}>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {ticket.description}
        </Typography>
      </SectionCard>
      {/* TMMPDM-0110 (U7): the files filed with the ticket, listed only when the server sends them. */}
      {ticket.attachments !== undefined && ticket.attachments.length > 0 ? (
        <SectionCard title={t('tickets.attachments.title', 'Attachments')}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('tickets.attachments.hint', 'Files are only downloaded, never opened here.')}
          </Typography>
          <TicketAttachments
            number={ticket.number}
            attachments={ticket.attachments}
            label={t('tickets.attachments.title', 'Attachments')}
          />
        </SectionCard>
      ) : null}
      <SectionCard title={t('tickets.details.title', 'Details')}>
        <Box
          component="dl"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
            columnGap: 3,
            rowGap: 1,
            m: 0,
          }}
        >
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>
    </Stack>
  );
}
